import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { KNBulletList } from 'components/KNBulletList/KNBulletList'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormFile from 'components/KNForm/KNFormFile'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { BulkCreateUsersDialogFormValues } from 'screens/UserManager/UserManager.types'
import { uploadBulkUsers, USERS_QUERY_KEY } from './UserManager.service'

interface BulkCreateUsersDialogProps {
  open: boolean
  onAction: () => void
  onClose: () => void
}

const BulkCreateUsersDialog = ({ open, onAction, onClose }: BulkCreateUsersDialogProps): ReactElement => {
  const { t } = useTranslation()
  const [createdUsers, setCreatedUsers] = useState<string[] | null>(null)
  const { handleSubmit, reset, control, formState, setValue, setError, clearErrors, watch } =
    useForm<BulkCreateUsersDialogFormValues>()
  const csv = watch('csv')
  const queryClient = useQueryClient()
  const uploadBulkUsersMutation = useMutation({
    mutationFn: uploadBulkUsers,
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          csv: null,
        }),
      )
      setCreatedUsers(null)
    }
  }, [open])

  const onSubmit: SubmitHandler<BulkCreateUsersDialogFormValues> = async (data: BulkCreateUsersDialogFormValues) => {
    setCreatedUsers(await uploadBulkUsersMutation.mutateAsync(data))
  }

  return (
    <KNDialog
      open={open}
      onClose={createdUsers ? onAction : onClose}
      closeLabel={createdUsers ? t('general.close') : t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.shared.user_manager.bulk_create_users.bulk_create')}
      actions={
        !createdUsers && (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting}
            disabled={!csv}
            onClick={handleSubmit(onSubmit)}
          >
            {t('general.send')}
          </Button>
        )
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      {createdUsers ? (
        <DialogContentText component="div">
          {createdUsers.length == 0 ? (
            <Typography color="text.main">
              {t('screens.shared.user_manager.bulk_create_users.no_new_users_created')}
            </Typography>
          ) : (
            <>
              <Typography color="text.main">
                {t('screens.shared.user_manager.bulk_create_users.created_users_list')}
              </Typography>
              <KNBulletList sx={{ color: 'text.main' }}>
                {createdUsers.map((createdUser) => (
                  <ListItem key={createdUser}>{createdUser}</ListItem>
                ))}
              </KNBulletList>
            </>
          )}
        </DialogContentText>
      ) : (
        <Stack spacing={2}>
          <KNFormFile
            label={t('screens.shared.user_manager.bulk_create_users.choose_file')}
            name="csv"
            control={control}
            rules={{ required: t('form.validation.required') }}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            accept={['text/csv']}
            size="small"
          />
        </Stack>
      )}
    </KNDialog>
  )
}

export default BulkCreateUsersDialog
